import { useQuery } from "@tanstack/react-query";
import { getCoachingClips } from "@/lib/api";
import { useScopedKey } from "@/lib/queryClient";
import { useScopeStore } from "@/stores/scope";

/**
 * The active scope's coaching clips for a single match — the data
 * behind Coach Review's clip rail and the clip-track existing-clips
 * lane.
 *
 * `getCoachingClips(matchId)` narrows to `?match_id=` on the backend
 * (`coach_clips.coach_list_clips`), which still applies the
 * team-scope check. The query key carries `match_id` in the `extra`
 * slot so each match's clips get their own cache bucket and the
 * `invalidateScope` predicate still sees a `[domain, teamId,
 * seasonId, …]` tuple — saving a clip (`useSaveCoachingClip`)
 * invalidates the whole scope, so the rail refetches after a new
 * clip lands.
 *
 * Mirrors `useCoachNotes`: `enabled` is gated on BOTH `teamId`
 * (scope must be reconciled — otherwise the request 409s) and
 * `matchId` (no match selected → nothing to fetch; the rail renders
 * its own empty state without a request).
 */
export function useCoachClips(matchId: string | undefined) {
  const teamId = useScopeStore((s) => s.teamId);
  return useQuery({
    queryKey: useScopedKey("coaching-clips", { match_id: matchId ?? null }),
    queryFn: () => getCoachingClips(matchId),
    enabled: !!teamId && !!matchId,
  });
}
